/**
 * /app/{slug}/people/org-chart — reporting-hierarchy tree
 * (G13 Vite port; 2026-06-05).
 *
 * Mirrors frontend/src/app/(dashboard)/dashboard/people/org-chart/page.tsx
 * exactly:
 *  - RBAC: HR-admin only (company_admin / hr_manager via isHRAdmin).
 *    Non-HR roles see a denial card with a link back to the directory.
 *  - Data: GET /api/v1/directory (active employees only). Backed by the
 *    shared useDirectory hook which returns manager_id for tree-building.
 *  - Rendering: pure CSS tree — flex layout, CSS border connectors, no
 *    canvas/SVG/D3. Root nodes sit at top; children in a centred flex row
 *    per parent. Nodes deeper than COLLAPSE_DEPTH are collapsed behind a
 *    "+N more" toggle (expand-on-click). "You" ring for the signed-in user.
 *  - Sub-nav: ModuleNav with Directory + Org chart tabs (mirrors the
 *    Next.js PeopleLayout). isHRAdmin-gated for the org-chart tab.
 *
 * Vite port swaps:
 *  - "use client" dropped
 *  - next/link → @tanstack/react-router Link (typed)
 *  - PageHeader, ModuleNav, isHRAdmin all from their Vite-dashboard paths
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Network, ShieldX, Users } from "lucide-react";
import { useDirectory } from "@/hooks/use-directory";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { toApiError } from "@/lib/api";
import { PageHeader } from "@/components/shared/page-header";
import { ModuleNav, type ModuleNavItem } from "@/components/shared/module-nav";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import type { DirectoryEntry } from "@/types/directory";

// Nodes at depth > COLLAPSE_DEPTH are hidden behind a toggle by default.
const COLLAPSE_DEPTH = 2; // 0-indexed (root=0, next=1, next=2, collapse=3+)

// ─── helpers ──────────────────────────────────────────────────────────────────

function initials(entry: DirectoryEntry): string {
  const parts = entry.display_name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === "") return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministic pastel hue from the user id so every user gets a
// consistent colour without a runtime lookup table.
const PALETTE = [
  "bg-indigo-100 text-indigo-700",
  "bg-violet-100 text-violet-700",
  "bg-sky-100 text-sky-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-teal-100 text-teal-700",
  "bg-orange-100 text-orange-700",
];

function avatarColour(id: number): string {
  return PALETTE[id % PALETTE.length];
}

// ─── tree building ─────────────────────────────────────────────────────────────

interface OrgNode {
  entry: DirectoryEntry;
  children: OrgNode[];
}

function buildTree(entries: DirectoryEntry[]): OrgNode[] {
  const active = entries.filter((e) => e.is_active);
  const byId = new Map<number, OrgNode>();
  for (const e of active) byId.set(e.id, { entry: e, children: [] });

  const roots: OrgNode[] = [];

  for (const node of byId.values()) {
    const mid = node.entry.manager_id;
    if (mid != null && byId.has(mid)) {
      byId.get(mid)!.children.push(node);
    } else {
      // No manager, or manager not in active set → root.
      roots.push(node);
    }
  }

  // Stable sort by display name at every level.
  function sortNodes(nodes: OrgNode[]) {
    nodes.sort((a, b) =>
      a.entry.display_name.localeCompare(b.entry.display_name),
    );
    for (const n of nodes) sortNodes(n.children);
  }
  sortNodes(roots);

  return roots;
}

// ─── OrgNode component ─────────────────────────────────────────────────────────

interface NodeProps {
  node: OrgNode;
  depth: number;
  myId: number | undefined;
}

function OrgNodeCard({ node, depth, myId }: NodeProps) {
  const { entry, children } = node;
  const [expanded, setExpanded] = useState(depth < COLLAPSE_DEPTH);
  const isMe = entry.id === myId;
  const collapseChildren = depth >= COLLAPSE_DEPTH;
  const hasChildren = children.length > 0;

  return (
    <div
      className="flex flex-col items-center"
      data-testid={`org-node-${entry.id}`}
    >
      {/* Card */}
      <div
        className={[
          "relative flex w-44 flex-col items-center gap-1.5 rounded-xl border bg-white px-3 py-3 shadow-sm text-center",
          isMe ? "ring-2 ring-indigo-500 ring-offset-1" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* Avatar */}
        <span
          className={[
            "flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold",
            avatarColour(entry.id),
          ].join(" ")}
          aria-hidden
        >
          {initials(entry)}
        </span>
        {/* Name */}
        <span className="text-xs font-semibold leading-tight text-gray-900 line-clamp-2">
          {entry.display_name}
          {isMe && (
            <span className="ml-1 rounded bg-indigo-100 px-1 py-0.5 text-[9px] uppercase tracking-wide text-indigo-700">
              You
            </span>
          )}
        </span>
        {/* Designation */}
        {entry.designation && (
          <span className="text-[10px] leading-tight text-muted-foreground line-clamp-1">
            {entry.designation}
          </span>
        )}
        {/* Department */}
        {entry.department && (
          <span className="rounded-full bg-secondary px-2 py-0.5 text-[9px] text-secondary-foreground">
            {entry.department}
          </span>
        )}
      </div>

      {/* Vertical connector + children */}
      {hasChildren && (
        <>
          {/* Vertical line down from card */}
          <div className="h-4 w-px bg-gray-300" aria-hidden />

          {/* Expand/collapse toggle when at the collapse threshold */}
          {collapseChildren ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-[10px] font-medium text-muted-foreground shadow-sm hover:bg-accent transition-colors"
              aria-expanded={expanded}
              aria-label={
                expanded
                  ? `Collapse ${children.length} report${children.length !== 1 ? "s" : ""}`
                  : `Expand ${children.length} report${children.length !== 1 ? "s" : ""}`
              }
              data-testid={`org-toggle-${entry.id}`}
            >
              {expanded
                ? `- ${children.length}`
                : `+ ${children.length} more`}
            </button>
          ) : null}

          {/* Children row */}
          {(!collapseChildren || expanded) && (
            <div className="relative flex flex-col items-center">
              {/* Horizontal spanning line across children */}
              {children.length > 1 && (
                <div
                  className="h-px bg-gray-300"
                  style={{
                    // Each child column is ~180px wide (w-44=176 + gap-6=24).
                    width: `${(children.length - 1) * 200}px`,
                    position: "relative",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                  aria-hidden
                />
              )}

              <div className="flex flex-row items-start gap-6 pt-0">
                {children.map((child) => (
                  <div
                    key={child.entry.id}
                    className="flex flex-col items-center"
                  >
                    {/* Short vertical line from horizontal to child card */}
                    <div className="h-4 w-px bg-gray-300" aria-hidden />
                    <OrgNodeCard
                      node={child}
                      depth={depth + 1}
                      myId={myId}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/people/org-chart",
)({
  component: OrgChartRoute,
});

function OrgChartRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const { user } = useAuth();
  const allowed = isHRAdmin(user?.role);

  const navItems: ModuleNavItem[] = useMemo(() => {
    const base: ModuleNavItem[] = [
      { label: "Directory", href: `/app/${tenantSlug}/people` },
    ];
    if (allowed) {
      base.push({
        label: "Org chart",
        href: `/app/${tenantSlug}/people/org-chart`,
      });
    }
    return base;
  }, [tenantSlug, allowed]);

  const dir = useDirectory({ includeInactive: false, enabled: allowed });

  const roots = useMemo(() => buildTree(dir.entries), [dir.entries]);

  // ── access denied ────────────────────────────────────────────────────
  if (!allowed) {
    return (
      <>
        <PageHeader
          title="Org chart"
          description="Visual reporting hierarchy for the company."
        />
        <ModuleNav items={[{ label: "Directory", href: `/app/${tenantSlug}/people` }]} />
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <ShieldX className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-base font-medium text-gray-900">
                You don&apos;t have access
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                The org chart is visible to HR managers and company admins
                only. Ask your HR team if you need to see the reporting
                structure.
              </p>
            </div>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/people" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to People
              </Link>
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  // ── loading ───────────────────────────────────────────────────────────
  if (dir.isLoading) {
    return (
      <>
        <PageHeader
          title="Org chart"
          description="Visual reporting hierarchy for the company."
        />
        <ModuleNav items={navItems} />
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders are positional
            <Skeleton key={i} className="h-28 w-44 rounded-xl" />
          ))}
        </div>
      </>
    );
  }

  // ── error ─────────────────────────────────────────────────────────────
  if (dir.isError) {
    return (
      <>
        <PageHeader
          title="Org chart"
          description="Visual reporting hierarchy for the company."
        />
        <ModuleNav items={navItems} />
        <Alert variant="destructive">
          <AlertTitle>Could not load the directory</AlertTitle>
          <AlertDescription>
            {toApiError(dir.error).message}
          </AlertDescription>
        </Alert>
      </>
    );
  }

  // ── empty ─────────────────────────────────────────────────────────────
  if (roots.length === 0) {
    return (
      <>
        <PageHeader
          title="Org chart"
          description="Visual reporting hierarchy for the company."
          actions={
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/people" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to People
              </Link>
            </Button>
          }
        />
        <ModuleNav items={navItems} />
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <Users className="h-6 w-6" />
            </span>
            <p className="max-w-sm text-sm text-muted-foreground">
              No active employees yet. Add your team first, then come back
              here to see the org chart.
            </p>
            <Button asChild className="gap-1.5">
              <Link to="/app/$tenantSlug/people/new" params={{ tenantSlug }}>
                <Users className="h-4 w-4" />
                Add employees
              </Link>
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  // ── tree ──────────────────────────────────────────────────────────────
  return (
    <>
      <PageHeader
        title="Org chart"
        description={`Reporting hierarchy · ${dir.entries.length} active employee${dir.entries.length !== 1 ? "s" : ""}`}
        actions={
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/people" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back to People
            </Link>
          </Button>
        }
      />

      <ModuleNav items={navItems} />

      {/* Hint bar */}
      <div className="mb-4 flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
        <Network className="h-3.5 w-3.5 shrink-0" />
        <span>
          Nodes deeper than 3 levels are collapsed — click the{" "}
          <strong>+N more</strong> button to expand a branch. Only active
          employees are shown.
        </span>
      </div>

      {/* Scrollable canvas */}
      <div
        className="overflow-auto rounded-xl border bg-gray-50 p-8"
        data-testid="org-chart-canvas"
      >
        <div className="flex flex-col items-center gap-8">
          {roots.map((root) => (
            <OrgNodeCard
              key={root.entry.id}
              node={root}
              depth={0}
              myId={user?.user_id}
            />
          ))}
        </div>
      </div>
    </>
  );
}
